import React from 'react';
import { useStepCollapse } from './useStepCollapse';
import { stepAnnouncement, visibleStepIndexes } from './stepWindow';

export type StepTone = 'idle' | 'active' | 'transit' | 'staged' | 'shipped' | 'done' | 'failed';
export type StepState = 'past' | 'current' | 'future';

export interface HeaderStep {
  key: string;
  label: string;
  /** Colour of the step when it is the current one (a workflow token, see design-system.css). */
  tone?: StepTone;
  /** Absent: the step is a plain label, not a button. */
  onSelect?: () => void;
  disabled?: boolean;
  /** Shown as "2  Label". */
  numbered?: boolean;
}

interface HeaderStatusStepsProps {
  steps: HeaderStep[];
  /** Index of the current step in `steps`; -1 when the status is not one of them. */
  currentIndex: number;
  ariaLabel: string;
  /**
   * 'ordered' marks the steps before the current one as past (a lifecycle);
   * 'choice' leaves them as plain alternatives (a vehicle is Available OR Assigned).
   */
  mode?: 'ordered' | 'choice';
}

/**
 * The chevron workflow tracker of the contextual bar. When the bar is too narrow
 * for every step it shows only the current step and the next one (the previous
 * and the current when the current is the last), then only the current one when
 * even two do not fit. It never wraps. The hidden steps stay in the page for
 * assistive technology ("Step 3 of 4: In Transit") and in the tooltip.
 */
const HeaderStatusSteps: React.FC<HeaderStatusStepsProps> = ({
  steps,
  currentIndex,
  ariaLabel,
  mode = 'ordered',
}) => {
  const stepsKey = steps.map(s => `${s.key}:${s.label}`).join('|');
  const { hostRef, level } = useStepCollapse(stepsKey, currentIndex);

  const shown = visibleStepIndexes(steps.length, currentIndex, level);
  const hidden = steps.map((_, i) => i).filter(i => !shown.includes(i));
  const collapsed = hidden.length > 0;
  const tooltip = steps.map((s, i) => (i === currentIndex ? `${s.label} (current)` : s.label)).join(' › ');

  const stateOf = (i: number): StepState => {
    if (i === currentIndex) return 'current';
    return mode === 'ordered' && i < currentIndex ? 'past' : 'future';
  };

  const labelOf = (s: HeaderStep, i: number) => (s.numbered ? `${i + 1}  ${s.label}` : s.label);

  const renderStep = (s: HeaderStep, i: number) => {
    const state = stateOf(i);
    const cls = `header-status-step header-status-step--${state}${state === 'current' && s.tone ? ` header-status-step--tone-${s.tone}` : ''}`;
    const aria = {
      'aria-current': state === 'current' ? ('step' as const) : undefined,
      'aria-label': stepAnnouncement(i, steps.length, s.label),
    };
    if (!s.onSelect) {
      return (
        <span key={s.key} className={cls} {...aria}>
          {labelOf(s, i)}
        </span>
      );
    }
    return (
      <button key={s.key} type="button" className={cls} disabled={s.disabled} onClick={s.onSelect} {...aria}>
        {labelOf(s, i)}
      </button>
    );
  };

  return (
    <div
      ref={hostRef}
      className="header-status-steps-host"
      data-collapsed={collapsed ? 'true' : undefined}
      title={collapsed ? tooltip : undefined}
    >
      <div className="header-status-steps" role="group" aria-label={ariaLabel}>
        {shown.map(i => renderStep(steps[i], i))}
      </div>
      {collapsed && (
        <ul className="header-status-steps__hidden" aria-label={`${ariaLabel}: other steps`}>
          {hidden.map(i => (
            <li key={steps[i].key}>
              {steps[i].onSelect ? (
                <button type="button" disabled={steps[i].disabled} onClick={steps[i].onSelect}>
                  {stepAnnouncement(i, steps.length, steps[i].label)}
                </button>
              ) : (
                stepAnnouncement(i, steps.length, steps[i].label)
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default HeaderStatusSteps;
